import React, { useEffect, useState, useCallback } from 'react';
import './transaction.scss';
import { Button, Card, Modal, Spin } from 'antd';
import transactionImage from '../../../../src/assets/images/transaction_image.svg';
import InvoiceImageFailure from '../../../../src/assets/images/invoice_download_icon.svg';
import InvoiceImageSuccess from '../../../../src/assets/images/invoice_download_success.svg';
import InvoiceImagePending from '../../../../src/assets/images/invoice_download_pending.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getTransaction } from '../../../slices/transaction/thunk';
import DateService from '../../../Services/DateService';
import { fetchData } from '../../../Services/FetchData';
import { API_ENDPOINTS } from '../../../constants/apiEndPoints';
import NoResults from '../../Components/NoResults/NoResults';
import Invoice from '../../../Common/Invoice/Invoice';
import Log from '../../../Services/Log';
import { getAdPlan } from '../../../slices/ads/thunk';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ShimmerCategoryItem } from "react-shimmer-effects";

document.title = "Transaction"

const Transaction = () => {
    const dispatch = useDispatch();
    const transactions = useSelector((state) => state.Transaction);
    const planActionData = useSelector((state) => state.AdPlans?.adPlans);

    let transactionStatus;
    const [transaction, setTransaction] = useState([]);
    const [activeTransactionId, setActiveTransactionId] = useState(null);
    const [activePriceExcludedGst, setActivePriceExcludedGst] = useState();
    const [invoicePreview, setInvoicePreview] = useState(false);
    const [paymentLogs, setPaymentLogs] = useState({});
    const [planData, setPlanData] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const limit = 8;
    const [loading, setLoading] = useState(false)
    const fetchMoreData = useCallback(() => {
        setPage(prevPage => prevPage + 1);
    }, []);

    const handleCancel = () => {
        setInvoicePreview(false);
        setPaymentLogs({});
        setActiveTransactionId(null);
    };

    const fetchInvoice = useCallback(async () => {
        if (!activeTransactionId) return;

        try {
            const endpoint = `${API_ENDPOINTS.PAYMENT.GET}${activeTransactionId}`;
            const response = await fetchData(endpoint);

            if (response && response._id) {
                Log('Fetched invoice data', 'info', response?.billing_address?.name);

                setPaymentLogs({
                    invoice_number: response.invoice_number,
                    payment_method: response.payment_gateway,
                    order_date: response.created_at,
                    billing_address: response.billing_address,
                    phone: response.mobile_number,
                    amount: response.amount ?? 0,
                    priceExcludedGst: 400,
                    transaction_id: response.transaction_id,
                    unit_price: response.unit_price,
                    product_title: response.product_type,
                    offer_price: response.amount,
                    priceExcludeGst: activePriceExcludedGst,
                    igst_value: response?.igst,
                    igst_amount: response.igst_amount ?? 0,
                    sgst: response?.sgst,
                    sgst_amount: response?.sgst_amount,
                    cgst: response?.cgst,
                    cgst_amount: response?.cgst_amount,
                    transactionId: response?.transactionId
                });

                setInvoicePreview(true);
                setLoading(false);
            } else {
                Log('No valid data received', 'warning');
            }
        } catch (error) {
            Log('Error fetching invoice data', 'error', error);
        }
    }, [activeTransactionId, activePriceExcludedGst]);

    useEffect(() => {
        fetchInvoice();
    }, [fetchInvoice]);

    useEffect(() => {
        if (planActionData.length === 0) {
            dispatch(getAdPlan());
        } else {
            setPlanData(planActionData);
        }
    }, [dispatch, planActionData]);

    useEffect(() => {
        if (transactions?.data?.length) {
            setTransaction(prev => {
                if (page === 1) {
                    return transactions.data
                }
                else {
                    let filteredCatalogs = transactions.data.filter((transaction) =>
                        !prev.some((prevAd) => prevAd._id === transaction._id)
                    );
                    return transactions.data.length > 0 ? [...prev, ...filteredCatalogs] : prev;
                }

            });
        }
        if (transactions.length < limit || transactions.length === 0) {
            setHasMore(false);
        }

    }, [transactions]);

    useEffect(() => {
        dispatch(getTransaction({ page, limit }));
    }, [dispatch, page, limit]);

    const handlePrintClick = (transaction) => {
        let status = transaction?.status;
        if (status === 'success') {
            setLoading(true)
            setActiveTransactionId(transaction?.transaction_id);
            const plan = planData.find(plan => plan.plan_id === transaction?.ads?.plan_id);
            setActivePriceExcludedGst(plan?.price);
        }

    };

    const shimmer = <>
        <Card>
            <ShimmerCategoryItem />
            <ShimmerCategoryItem
                hasImage
                imageType="thumbnail"
                imageWidth={100}
                imageHeight={100}
                title
            />

            <ShimmerCategoryItem
                hasImage
                imageType="circular"
                imageWidth={100}
                imageHeight={100}
                title
            />

            <ShimmerCategoryItem
                hasImage
                imageType="thumbnail"
                imageWidth={100}
                imageHeight={100}
                text
            />

            <ShimmerCategoryItem
                hasImage
                imageType="circular"
                imageWidth={100}
                imageHeight={100}
                text
            />

            <ShimmerCategoryItem
                hasImage
                imageType="thumbnail"
                imageWidth={100}
                imageHeight={100}
                text
                cta
            />

            <ShimmerCategoryItem
                hasImage
                imageType="circular"
                imageWidth={100}
                imageHeight={100}
                text
                cta
            />
        </Card>
    </>

    return (
        <>
            {(loading) && (
                <div className="spinner center">
                    <Spin />
                </div>
            )}
            <InfiniteScroll
                dataLength={transaction.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                    transactions.loading && (
                        <div className="spinner center" key={0}>
                            <Spin />
                        </div>
                    )
                }

            // endMessage={<p className='no_more_text'>No more items to load</p>}
            >
                <section className="transaction">
                    {transaction.length > 0 ? (
                        transaction.map((el) => (
                            <Card key={el.transaction_id} className='transaction_item'>
                                <div className="left">
                                    <figure>
                                        <img src={el.ads?.ad_images[0]?.thumbnail} className="img_full cover" alt="" loading="lazy" />
                                    </figure>
                                    <div className="cnt">
                                        <span className='text_light'>
                                            <span>Ad Id :&nbsp;</span>
                                            <span>{el.ad_id}</span>
                                        </span>
                                        <span className="title">Created on {DateService.formatDateCustom(el.created_at, true)}</span>
                                    </div>
                                </div>
                                <div className="middle">
                                    <div className="cnt">
                                        <span>
                                            <span>Plan </span><span className="text_red">: {el?.plan}</span>
                                        </span>
                                        {
                                            el.ads?.end_date && (
                                                <span>
                                                    <span>Due </span><span>: {DateService.formatDateCustom(el.ads?.end_date)} </span>
                                                </span>
                                            )
                                        }
                                        <span className={` transaction_status ${el?.status?.toLowerCase()}`}>
                                            {el?.status}
                                        </span>

                                    </div>
                                </div>
                                <div className="right">
                                    <div className="cnt print">
                                        {
                                            (() => {
                                                transactionStatus = (() => {
                                                    switch (el?.status) {
                                                        case "pending":
                                                            return 'pending';
                                                        case "success":
                                                            return 'completed';
                                                        case "failed":
                                                            return 'failed';
                                                        case "expired":
                                                            return 'pending'
                                                        default:
                                                            return "pending";
                                                    }
                                                })();
                                            })()
                                        }
                                        <a href="#" className={`${transactionStatus} invoice_btn`} onClick={() => handlePrintClick(el)}>
                                            <figure>
                                                {
                                                    (() => {
                                                        switch (transactionStatus) {
                                                            case "pending":
                                                                return (
                                                                    <img src={InvoiceImagePending} alt="Pending" />
                                                                );
                                                            case "completed":
                                                                return (
                                                                    <img src={InvoiceImageSuccess} alt="Completed" />
                                                                );
                                                            case "failed":
                                                                return (
                                                                    <img src={InvoiceImageFailure} alt="Failure" />
                                                                );
                                                            default:
                                                                return (
                                                                    <img src={InvoiceImagePending} alt="Pending" />
                                                                );
                                                        }
                                                    })()
                                                }
                                            </figure>
                                        </a>
                                        <span className="price">
                                            <span>{el?.amount}</span>
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        ))
                    ) : (
                        !transactions.loading && (
                            <NoResults
                                className='transaction_no_results bg_white'
                                type='transaction'
                                title='No Transactions Yet'
                                description='Complete your payment to kickstart the promotion of your offerings.'
                            />
                        )

                    )}
                    <Modal afterClose={() => (document.body.style.overflow = 'initial')} className='invoice_modal' open={invoicePreview} footer={<Button key="close" type="text" className="short invoice_modal custom_btn" onClick={handleCancel}>
                        Close
                    </Button>} onCancel={handleCancel}>
                        <Invoice paymentLogs={paymentLogs} preview={invoicePreview} />
                    </Modal>
                </section>
            </InfiniteScroll>
        </>
    );
};

export default Transaction;
